import Phaser from 'phaser';
import { HAPTICS, PALETTE, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Phase = 'intro' | 'showcase' | 'power' | 'angle' | 'flight' | 'between' | 'done';

const CIRCLE_X = 150;
const PPM = SPRINT.pixelsPerMeter;
const ANGLE_MIN = 24;
const ANGLE_MAX = 56;
const ANGLE_TARGET = 0.5; // ~40° on the sweep
const ATTEMPTS = 3;

/**
 * Shot Put — no run-up: a standing power throw. Stop the rising/falling POWER bar near
 * the top, then lock the ANGLE on its sweep (~40° is ideal). Distance = power × angle
 * quality × strength. Short, heavy, satisfying arcs (~<24 m). AI fill the field.
 */
export class ShotPut extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private power = 0;
  private angleSweep = 0;
  private lockedPower = 0;
  private lockedAngle = 0;
  private reach = 18;
  private throwDist = 0;
  private flightT = 0;
  private flightDur = 1;
  private releaseX = 0;
  private releaseY = 0;
  private shot!: Phaser.GameObjects.Graphics;
  private aim!: Phaser.GameObjects.Graphics;
  private attempt = 1;
  private putBest = 0;
  private aiIndex = 0;
  private aiRig?: AthleteView;
  private playerMarker?: Phaser.GameObjects.GameObject[];

  private W = 0; private H = 0; private groundY = 0; private uiS = 1; private camTarget = 0;
  private aiThrows: { name: string; species: string; nationality: string; metric: number; skin: string }[] = [];

  private powerBar!: PowerMeter;
  private angleBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private actBtn!: Button;

  constructor() { super('ShotPut'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.power = 0; this.angleSweep = 0; this.throwDist = 0; this.flightT = 0; this.camTarget = 0; this.lastAction = 0;
    this.attempt = 1; this.putBest = 0; this.aiIndex = 0; this.playerMarker = undefined;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.2);
    this.reach = 14 + (this.athlete.stats.power / 100) * 12;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.rig = createAthleteView(this, CIRCLE_X, this.groundY, this.athlete, Phaser.Math.Clamp(h * 0.36, 180, 340));
    this.rig.container.setDepth(6);
    this.shot = this.add.graphics().setDepth(8);
    this.aim = this.add.graphics().setDepth(7);
    this.fetchWorldRecord('shot_put', 'distance', (m) => this.drawGroundRecord(CIRCLE_X + 30 + m * PPM, this.groundY, this.groundY - Math.max(70, this.H * 0.18), `${m.toFixed(2)}m`));

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiThrows = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, skin: o.skin, metric: Math.max(8, 11 + (o.jumpDistance - 4) * 1.6 + (Math.random() * 3 - 1.5)) }));

    this.buildHud();
    this.controls.on('action', () => this.onAction());
    this.controls.on('tapLeft', () => this.onAction());
    this.controls.on('tapRight', () => this.onAction());
    this.startCountdown();
  }

  private drawField(): void {
    const endX = CIRCLE_X + 30 * PPM;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(0x4e8b4e, 1).fillRect(0, this.groundY, endX, this.H - this.groundY); // grass sector
    // Throwing circle.
    g.fillStyle(0xcfcfd6, 1).fillEllipse(CIRCLE_X, this.groundY + 6, 70, 22);
    g.lineStyle(3, 0xf4f7ff, 0.9).strokeEllipse(CIRCLE_X, this.groundY + 6, 70, 22);
    g.fillStyle(PALETTE.white, 1).fillRect(CIRCLE_X + 34, this.groundY - 2, 6, 16); // toe board
    for (let m = 5; m <= 28; m += 5) {
      const x = CIRCLE_X + m * PPM;
      g.fillStyle(0x66a366, 1).fillRect(x, this.groundY, 2, 14);
      this.add.text(x, this.groundY + 18, `${m}m`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: '#cfe6cf' }).setOrigin(0.5, 0).setDepth(-6);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(26), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(280, this.W * 0.42);
    const topY = Math.max(84, this.H * 0.15); // below the top-right quit button
    this.powerBar = new PowerMeter(this, 30 + barW / 2, topY, { width: barW, height: 18 });
    this.add.text(30 + barW / 2, topY - 18, t('hud.power'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.angleBar = new PowerMeter(this, this.W - 30 - barW / 2, topY, { width: barW, height: 18, target: [ANGLE_TARGET - 0.12, ANGLE_TARGET + 0.12], fillColor: PALETTE.cadence });
    this.add.text(this.W - 30 - barW / 2, topY - 18, t('hud.angle'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.angleBar.setVisible(false);
    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(80), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `💪 ${t('btn.stop')} ${t('btn.space')}` : `💪 ${t('btn.stop')}`;
    this.actBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.onAction(), {
      width: Math.min(210, this.W * 0.42), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.actBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
          this.runAiShowcase();
        } else AudioEngine.countdownTick();
      });
    });
  }

  private lastAction = 0;

  /** Each rival's animal steps into the circle and puts the shot to their best, then a flag. */
  private runAiShowcase(): void {
    this.phase = 'showcase';
    if (this.aiIndex >= this.aiThrows.length) { this.rig.container.setVisible(true); this.beginAttempt(); return; }
    this.rig.container.setVisible(false);
    const a = this.aiThrows[this.aiIndex]!;
    this.hud.setText(`💪 ${a.name}`);
    this.aiRig?.destroy();
    this.aiRig = createAthleteView(this, CIRCLE_X, this.groundY, { skin: a.skin }, Phaser.Math.Clamp(this.H * 0.36, 180, 340));
    this.aiRig.container.setDepth(6);
    this.camTarget = 0;
    this.cameras.main.scrollX = 0;
    // Brief wind-up, then the put.
    this.time.delayedCall(450, () => { this.aiRig?.setPose('celebrate'); this.aiThrowArc(a); });
  }

  private aiThrowArc(a: { name: string; nationality: string; metric: number }): void {
    const landX = CIRCLE_X + 30 + a.metric * PPM;
    const releaseX = CIRCLE_X + 30, releaseY = this.groundY - this.H * 0.18;
    const obj = { p: 0 };
    AudioEngine.whoosh();
    this.tweens.add({
      targets: obj, p: 1, duration: 650, ease: 'Sine.in',
      onUpdate: () => {
        const x = Phaser.Math.Linear(releaseX, landX, obj.p);
        const peak = this.H * 0.3 * Math.sin(Phaser.Math.DegToRad(40));
        const y = releaseY - Math.sin(obj.p * Math.PI) * peak + (this.groundY - releaseY) * obj.p;
        this.shot.clear();
        this.shot.fillStyle(0x3a3f50, 1).fillCircle(x, y, 11);
        this.shot.fillStyle(0x8b93a8, 0.9).fillCircle(x - 3, y - 3, 4);
        this.camTarget = Phaser.Math.Clamp(x - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
      },
      onComplete: () => {
        this.shot.clear();
        this.flagMarker(landX, PALETTE.cadence, `${countryFlag(a.nationality)} ${a.name.split(' ')[0]} · ${a.metric.toFixed(2)}m`, false);
        AudioEngine.thud();
        this.tweens.add({ targets: this.aiRig!.container, alpha: 0, duration: 250, onComplete: () => { this.aiRig?.destroy(); this.aiRig = undefined; this.aiIndex++; this.runAiShowcase(); } });
      },
    });
  }

  private beginAttempt(): void {
    this.power = 0; this.angleSweep = 0; this.lockedPower = 0; this.lockedAngle = 0; this.flightT = 0; this.lastAction = this.time.now;
    this.phase = 'power';
    this.shot.clear();
    this.aim.clear();
    this.bigText.setAlpha(0);
    this.rig.container.setVisible(true).setAlpha(1);
    this.rig.setPose('idle');
    this.controls.setEnabled(true);
    this.actBtn.setText(this.sys.game.device.os.desktop ? `💪 ${t('btn.stop')} ${t('btn.space')}` : `💪 ${t('btn.stop')}`).setVisible(true);
    this.powerBar.setVisible(true);
    this.angleBar.setVisible(false);
    this.camTarget = 0;
    this.cameras.main.scrollX = 0;
    this.hud.setText(t('game.attemptPower', { n: this.attempt, max: ATTEMPTS }));
  }

  private flagMarker(x: number, color: number, label: string, mine: boolean): Phaser.GameObjects.GameObject[] {
    const poleH = Math.max(46, this.H * 0.11);
    const top = this.groundY - poleH;
    const g = this.add.graphics().setDepth(mine ? 6 : 4);
    g.fillStyle(0xffffff, 0.85).fillRect(x - 1.5, top, 3, poleH);
    g.fillStyle(color, 1).fillTriangle(x + 1.5, top, x + 1.5, top + 18, x + 26, top + 9);
    g.fillStyle(color, 1).fillCircle(x, this.groundY, 4);
    const t = this.add.text(x, top - 6, label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(12 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: mine ? '#ffd24a' : '#bfe6ff', padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(mine ? 7 : 5);
    return [g, t];
  }

  private onAction(): void {
    // A single mobile tap fires both the button AND the scene tap-zone (both routed here);
    // debounce so one tap can't lock power AND angle at once (skipping the angle phase).
    const now = this.time.now;
    if (now - this.lastAction < 280) return;
    this.lastAction = now;
    if (this.phase === 'power') {
      this.lockedPower = this.power;
      this.phase = 'angle';
      this.angleBar.setVisible(true);
      this.actBtn.setText(`🎯 ${t('btn.lockAngle')}`);
      this.hud.setText(t('game.lockAngleHud'));
      AudioEngine.tap(0.6);
      this.vibrate(HAPTICS.goodTap);
    } else if (this.phase === 'angle') {
      this.lockedAngle = this.angleSweep;
      this.release();
    }
  }

  /** Aim arrow from the athlete's shoulder showing the current launch angle. */
  private drawAim(norm: number): void {
    const deg = ANGLE_MIN + norm * (ANGLE_MAX - ANGLE_MIN);
    const rad = Phaser.Math.DegToRad(deg);
    const ox = CIRCLE_X + 24;
    const oy = this.groundY - this.H * 0.2;
    const len = Math.max(70, this.H * 0.2);
    const ex = ox + Math.cos(rad) * len;
    const ey = oy - Math.sin(rad) * len;
    const good = Math.abs(norm - ANGLE_TARGET) < 0.12;
    const col = good ? PALETTE.good : PALETTE.cadence;
    const g = this.aim;
    g.clear();
    g.lineStyle(4, col, 0.9).beginPath();
    g.moveTo(ox, oy); g.lineTo(ex, ey); g.strokePath();
    // Arrowhead.
    const a1 = rad + Math.PI * 0.85; const a2 = rad - Math.PI * 0.85;
    g.fillStyle(col, 0.9).fillTriangle(ex, ey, ex + Math.cos(a1) * 14, ey - Math.sin(a1) * 14, ex + Math.cos(a2) * 14, ey - Math.sin(a2) * 14);
  }

  private release(): void {
    this.controls.setEnabled(false);
    this.actBtn.setVisible(false);
    this.aim.clear();
    const angleQ = Phaser.Math.Clamp(1 - Math.abs(this.lockedAngle - ANGLE_TARGET) / 0.5, 0.2, 1);
    this.throwDist = Math.max(2, (3 + this.lockedPower * this.reach) * (0.55 + 0.45 * angleQ));
    const angleDeg = ANGLE_MIN + this.lockedAngle * (ANGLE_MAX - ANGLE_MIN);
    this.releaseX = CIRCLE_X + 30;
    this.releaseY = this.groundY - this.H * 0.18;
    this.phase = 'flight';
    this.flightT = 0;
    this.flightDur = Phaser.Math.Clamp(this.throwDist / 16, 0.7, 1.6);
    this.releaseAngleRad = Phaser.Math.DegToRad(angleDeg);
    this.rig.setPose('celebrate');
    AudioEngine.whoosh();
    this.vibrate(HAPTICS.goodTap);
  }

  private releaseAngleRad = 0;

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    this.rig.update(dt);
    if (this.phase === 'power') {
      this.power = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * 3.4 - Math.PI / 2);
      this.powerBar.set(this.power);
    } else if (this.phase === 'angle') {
      this.angleSweep = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * 3.0);
      this.angleBar.set(this.angleSweep, this.angleSweep);
      this.powerBar.set(this.lockedPower);
      this.drawAim(this.angleSweep);
    } else if (this.phase === 'flight') {
      this.updateFlight(dt);
    }
  }

  private updateFlight(dt: number): void {
    this.flightT += dt;
    const p = Phaser.Math.Clamp(this.flightT / this.flightDur, 0, 1);
    const landX = CIRCLE_X + 30 + this.throwDist * PPM;
    const x = Phaser.Math.Linear(this.releaseX, landX, p);
    const peak = this.H * 0.3 * Math.sin(this.releaseAngleRad);
    const y = this.releaseY - Math.sin(p * Math.PI) * peak + (this.groundY - this.releaseY) * p;
    this.shot.clear();
    this.shot.fillStyle(0x3a3f50, 1).fillCircle(x, y, 11);
    this.shot.fillStyle(0x8b93a8, 0.9).fillCircle(x - 3, y - 3, 4);
    this.camTarget = Phaser.Math.Clamp(x - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
    if (p >= 1) this.land();
  }

  private land(): void {
    this.phase = 'between';
    const landX = CIRCLE_X + 30 + this.throwDist * PPM;
    this.cameras.main.shake(140, 0.005);
    AudioEngine.thud();
    this.vibrate(HAPTICS.finish);
    const dust = this.add.particles(landX, this.groundY, 'spark', {
      lifespan: 520, speed: { min: 30, max: 120 }, angle: { min: 200, max: 340 },
      scale: { start: 0.5, end: 0 }, alpha: { start: 0.6, end: 0 }, tint: 0x9a9a4e, gravityY: 320, emitting: false,
    });
    dust.explode(14);
    this.time.delayedCall(800, () => dust.destroy());

    const isBest = this.throwDist > this.putBest;
    if (isBest) {
      this.putBest = this.throwDist;
      this.playerMarker?.forEach((o) => o.destroy());
      this.playerMarker = this.flagMarker(landX, PALETTE.sun, `${countryFlag(this.athlete.nationality)} ${this.athlete.name.split(' ')[0]} · ${this.putBest.toFixed(2)}m`, true);
      AudioEngine.fanfare();
    }
    this.bigText.setText(`${this.throwDist.toFixed(2)} m${isBest ? '  ⭐' : ''}`).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 450, ease: 'Back.out' });
    this.hud.setText(t('game.attemptBest', { n: this.attempt, max: ATTEMPTS, m: this.putBest.toFixed(2) }));

    if (this.attempt < ATTEMPTS) {
      this.attempt++;
      this.time.delayedCall(1700, () => this.beginAttempt());
    } else {
      this.time.delayedCall(1900, () => this.finalize());
    }
  }

  private finalize(): void {
    this.phase = 'done';
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.putBest },
      ...this.aiThrows.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(this.putBest * 42, 0, 1000));
    const reward = getDiscipline('shot_put').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'shot_put', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.putBest, score, reward, rank, standings,
    };
    void this.finishDiscipline(result);
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
